import { toPixels } from './grid.js'
import { FONT, FONT_SIZE, LINE_HEIGHT, GRID_SIZE, TEXT_PADDING_X_UNITS, TEXT_PADDING_TOP_PX, TEXT_PADDING_BOTTOM_PX, TEXT_BOTTOM_TRIM_PX, CSS_BASELINE_ADJUST_PX, TEXT_BASELINE_SHIFT_PX, MOBILE_EDITOR_DONE_BENTO_HEIGHT_PX, MOBILE_EDITOR_DONE_GAP_PX, SNIPPET_BOUNDARY_CHARS } from './constants.js'
import { findSnippetAtCaret } from './document.js'
import type { Block, Camera, PaperSpec } from './types.js'

export class Input {
  private overlay: HTMLDivElement
  private highlight: HTMLDivElement
  private mobileSheet: HTMLDivElement
  private mobileDoneBento: HTMLDivElement
  private mobileText: HTMLTextAreaElement
  private mobileDone: HTMLButtonElement
  private mobileLayout?: { initialHeight: number, maxHeight: number }
  private isEditing = false
  private mode: 'desktop' | 'mobile' = 'desktop'
  private isTouchDevice = (navigator.maxTouchPoints || 0) > 0
  private currentBlockId?: string
  private onTextChange?: (id: string, text: string) => void
  private onStopEditingCb?: () => void
  private onEnterCb?: (split: { before: string, after: string, dropTrailingBreak?: boolean }) => void
  private camera: Camera = { scale: 1, tx: 0, ty: 0 }
  private pendingCaretPosition?: { clientX: number, clientY: number }
  private stoppedByPointer = false
  private mobileCaretAtStart = false
  // Volatile snippet-revert record: immediate backspace after an expansion
  // restores the literal trigger; any other input invalidates it.
  private pendingSnippetRevert?: { blockId: string, inserted: string, original: string }
  private onWheelCb?: (e: WheelEvent) => void

  constructor() {
    this.highlight = this.createHighlight()
    this.overlay = this.createOverlay()
    const { sheet, doneBento, textarea, done } = this.createMobileEditor()
    this.mobileSheet = sheet
    this.mobileDoneBento = doneBento
    this.mobileText = textarea
    this.mobileDone = done
    document.body.appendChild(this.highlight)
    document.body.appendChild(this.overlay)
    document.body.appendChild(this.mobileSheet)
    document.body.appendChild(this.mobileDoneBento)
    this.setupEventListeners()
  }

  private createHighlight(): HTMLDivElement {
    const highlight = document.createElement('div')
    highlight.style.position = 'fixed'
    highlight.style.visibility = 'hidden'
    highlight.style.pointerEvents = 'none'
    highlight.style.background = 'var(--surface)'
    highlight.style.opacity = '1'
    highlight.style.boxShadow = 'var(--cmd-shadow-surface)'
    highlight.style.margin = '0'
    highlight.style.padding = '0'
    highlight.style.border = 'none'
    highlight.style.borderRadius = 'var(--radius)'
    highlight.style.transformOrigin = 'top left'
    highlight.style.zIndex = '999'
    return highlight
  }

  private createOverlay(): HTMLDivElement {
    const overlay = document.createElement('div')
    overlay.contentEditable = 'true'
    overlay.style.position = 'fixed'
    overlay.style.visibility = 'hidden'
    overlay.style.border = 'none'
    overlay.style.outline = 'none'
    overlay.style.background = 'transparent'
    overlay.style.pointerEvents = 'auto'
    overlay.style.resize = 'none'
    overlay.style.overflow = 'hidden'
    overlay.style.whiteSpace = 'pre-wrap'
    overlay.style.fontFamily = FONT
    // Keep editor font constant at 16px to avoid iOS auto-zoom; scale via transform instead
    overlay.style.fontSize = '16px'
    overlay.style.lineHeight = `${LINE_HEIGHT}px`  // 32px (2 grid units)
    overlay.style.color = 'var(--text-primary)'
    overlay.style.caretColor = 'var(--sync-blue)'  // Complementary blue for visibility
    overlay.style.zIndex = '1000'
    overlay.style.boxShadow = 'none'
    // Explicit zero spacing to ensure alignment with canvas block bounds
    overlay.style.padding = '0'
    overlay.style.margin = '0'
    ;(overlay.style as any).borderRadius = 'var(--radius)'
    ;(overlay.style as any).transformOrigin = 'top left'
    overlay.style.wordBreak = 'break-word'
    overlay.style.overflowWrap = 'anywhere'
    
    return overlay
  }

  private createMobileEditor(): { sheet: HTMLDivElement, doneBento: HTMLDivElement, textarea: HTMLTextAreaElement, done: HTMLButtonElement } {
    const sheet = document.createElement('div')
    sheet.style.position = 'fixed'
    sheet.style.left = '50%'
    sheet.style.top = `calc(${GRID_SIZE * 2}px + env(safe-area-inset-top, 0px))` // mirror cmd/ inset
    sheet.style.transform = 'translateX(-50%)'
    sheet.style.background = 'var(--surface)'
    sheet.style.borderRadius = 'var(--radius)'
    sheet.style.padding = '16px 8px 16px 16px'
    sheet.style.boxShadow = 'var(--cmd-shadow-surface)'
    sheet.style.display = 'none'
    sheet.style.position = 'fixed'
    sheet.style.overflow = 'visible'
    sheet.style.flexDirection = 'column'
    sheet.style.gap = '12px'
    // Keep mobile editor above command palette while still below auth modals.
    sheet.style.zIndex = '2500'
    sheet.style.touchAction = 'pan-y'
    const panelClamp = `clamp(var(--panel-min-touch, ${GRID_SIZE * 18}px), calc(100vw - var(--panel-gutter-touch, ${GRID_SIZE * 4}px) - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px)), var(--panel-max-touch, ${GRID_SIZE * 44}px))`
    sheet.style.width = panelClamp
    sheet.style.maxWidth = panelClamp
    sheet.style.boxSizing = 'border-box'

    const doneBento = document.createElement('div')
    doneBento.className = 'mobile-editor-done-bento'
    doneBento.style.position = 'fixed'
    doneBento.style.left = '50%'
    doneBento.style.transform = 'translateX(-50%)'
    doneBento.style.display = 'none'
    doneBento.style.width = panelClamp
    doneBento.style.maxWidth = panelClamp
    doneBento.style.height = `${MOBILE_EDITOR_DONE_BENTO_HEIGHT_PX}px`
    doneBento.style.boxSizing = 'border-box'
    doneBento.style.zIndex = '2500'
    doneBento.style.touchAction = 'manipulation'

    const textarea = document.createElement('textarea')
    textarea.style.width = '100%'
    textarea.style.resize = 'none'
    textarea.style.border = 'none'
    textarea.style.outline = 'none'
    textarea.style.background = 'transparent'
    textarea.style.color = 'var(--text-primary)'
    textarea.style.fontFamily = FONT
    textarea.style.fontSize = `${FONT_SIZE}px`
    textarea.style.lineHeight = `${LINE_HEIGHT}px`
    textarea.style.caretColor = 'var(--sync-blue)'
    textarea.style.padding = '0 8px 0 0'
    textarea.style.margin = '0'
    textarea.style.boxSizing = 'border-box'
    textarea.style.overflowY = 'auto'
    textarea.style.whiteSpace = 'pre-wrap'
    textarea.style.wordBreak = 'break-word'
    textarea.style.overflowWrap = 'anywhere'
    textarea.style.touchAction = 'pan-y'

    const done = document.createElement('button')
    done.type = 'button'
    done.className = 'mobile-editor-done'
    done.textContent = 'done'
    done.setAttribute('aria-label', 'Done editing')
    done.style.width = '100%'
    done.style.minHeight = `${GRID_SIZE * 2}px`
    done.style.height = `${GRID_SIZE * 2}px`
    done.style.border = 'none'
    done.style.fontFamily = FONT
    done.style.fontSize = `${FONT_SIZE}px`
    done.style.lineHeight = `${LINE_HEIGHT}px`
    done.style.textAlign = 'center'
    done.style.cursor = 'pointer'
    done.style.padding = '0 12px'

    const preventZoom = (e: Event) => {
      const touchEvent = e as TouchEvent
      if (touchEvent.touches && touchEvent.touches.length > 1) {
        e.preventDefault()
      }
    }
    sheet.addEventListener('touchstart', preventZoom, { passive: false })
    sheet.addEventListener('gesturestart', (e) => e.preventDefault(), { passive: false } as any)
    sheet.addEventListener('gesturechange', (e) => e.preventDefault(), { passive: false } as any)
    sheet.addEventListener('gestureend', (e) => e.preventDefault(), { passive: false } as any)

    sheet.appendChild(textarea)
    doneBento.appendChild(done)
    return { sheet, doneBento, textarea, done }
  }

  onWheel(cb: (e: WheelEvent) => void): void {
    this.onWheelCb = cb
  }

  private setupEventListeners(): void {
    this.overlay.addEventListener('blur', () => {
      // Window-level blur (cmd-tab, devtools) keeps the editing session
      // alive; the browser restores focus and caret on return. Only an
      // in-page focus steal (a pointer press elsewhere) ends editing.
      if (!document.hasFocus()) return
      this.stopEditing('pointer')
    })

    // Forward wheel to the app's canvas pan/zoom so the camera stays live
    // while the cursor sits over the block being edited.
    this.overlay.addEventListener('wheel', (e: WheelEvent) => {
      if (this.onWheelCb) this.onWheelCb(e)
    }, { passive: false })

    this.overlay.addEventListener('paste', (e) => {
      e.preventDefault()
      this.pendingSnippetRevert = undefined
      const text = e.clipboardData?.getData('text/plain') ?? ''
      this.insertTextAtCaret(text)
    })

    this.overlay.addEventListener('input', () => {
      if (this.currentBlockId && this.onTextChange) {
        this.onTextChange(this.currentBlockId, this.overlay.innerText)
      }
    })

    this.overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.stopEditing()
        return
      }
      if (e.key === 'Backspace') {
        if (this.revertSnippetDesktop()) {
          e.preventDefault()
          return
        }
        this.pendingSnippetRevert = undefined
        return
      }
      if (e.key === 'Enter') {
        this.pendingSnippetRevert = undefined
        if (e.shiftKey) {
          e.preventDefault()
          if (this.onEnterCb) this.onEnterCb(this.getSplitForDesktop())
          return
        }
        // Plain Enter: insert inline newline and keep editing
        e.preventDefault()
        if (!this.expandSnippetDesktop('\n')) {
          this.insertTextAtCaret('\n')
        }
        return
      }
      if (e.key.length === 1) {
        if (SNIPPET_BOUNDARY_CHARS.includes(e.key) && this.expandSnippetDesktop(e.key)) {
          e.preventDefault()
          return
        }
        this.pendingSnippetRevert = undefined
      }
    })

    this.mobileText.addEventListener('input', (e) => {
      const inputEvent = e as InputEvent
      const data = inputEvent.data
      // Expand on the input event (not keydown) so virtual keyboards that
      // skip per-key keydown still trigger; paste stays literal by design.
      if (inputEvent.inputType === 'insertText' && typeof data === 'string' && data.length === 1 && SNIPPET_BOUNDARY_CHARS.includes(data)) {
        this.expandSnippetMobileAfterBoundary()
      } else {
        this.pendingSnippetRevert = undefined
      }
      if (this.currentBlockId && this.onTextChange) {
        this.onTextChange(this.currentBlockId, this.mobileText.value)
      }
      this.syncMobileLayout()
    })

    this.mobileText.addEventListener('paste', (e) => {
      e.preventDefault()
      this.pendingSnippetRevert = undefined
      const text = e.clipboardData?.getData('text/plain') ?? ''
      this.insertTextIntoMobileEditor(text)
    })

    this.mobileText.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.stopEditing()
        return
      }
      if (e.key === 'Backspace') {
        if (this.revertSnippetMobile()) {
          e.preventDefault()
        }
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        const caret = this.mobileText.selectionEnd ?? this.mobileText.value.length
        const collapsed = caret === (this.mobileText.selectionStart ?? caret)
        if (collapsed && this.applyMobileSnippet(caret, '\n', caret)) {
          if (this.currentBlockId && this.onTextChange) {
            this.onTextChange(this.currentBlockId, this.mobileText.value)
          }
          this.syncMobileLayout()
          return
        }
        // Single Enter: insert newline and keep editing
        const value = this.mobileText.value
        const next = value.slice(0, caret) + '\n' + value.slice(caret)
        this.mobileText.value = next
        const nextCaret = caret + 1
        this.mobileText.setSelectionRange(nextCaret, nextCaret)
        if (this.currentBlockId && this.onTextChange) {
          this.onTextChange(this.currentBlockId, this.mobileText.value)
        }
        this.syncMobileLayout()
        return
      }
    })

    this.mobileText.addEventListener('blur', () => {
      // Keep sheet open when keyboard hides; exit via explicit control
      if (this.mode !== 'mobile') {
        this.stopEditing()
      }
    })

    this.mobileDone.addEventListener('click', () => {
      this.stopEditing()
    })

    const vv: any = (window as any).visualViewport
    if (vv && typeof vv.addEventListener === 'function') {
      vv.addEventListener('resize', () => {
        this.syncMobileLayout()
      }, { passive: true } as any)
    }
  }

  startEditing(block: Block, onChange: (id: string, text: string) => void, onStop?: () => void, onEnter?: (split: { before: string, after: string, dropTrailingBreak?: boolean }) => void, caretScreenPosition?: { clientX: number, clientY: number }, mobileCaretAtStart?: boolean): void {
    this.currentBlockId = block.ided[0]
    this.onTextChange = onChange
    this.onStopEditingCb = onStop
    this.onEnterCb = onEnter
    this.pendingSnippetRevert = undefined
    this.isEditing = true
    this.pendingCaretPosition = caretScreenPosition
    this.mobileCaretAtStart = Boolean(mobileCaretAtStart)
    this.mode = this.isTouchDevice ? 'mobile' : 'desktop'
    
    if (this.mode === 'mobile') {
      this.startMobileEditing(block)
      return
    }

    this.startDesktopEditing(block)
  }

  stopEditing(reason?: 'pointer'): void {
    if (!this.isEditing) return

    this.commitSnippetAtEnd()
    this.pendingSnippetRevert = undefined
    this.isEditing = false
    this.stoppedByPointer = reason === 'pointer'
    this.currentBlockId = undefined
    this.onTextChange = undefined
    const stopCb = this.onStopEditingCb
    this.onStopEditingCb = undefined
    if (this.mode === 'desktop') {
      this.overlay.style.visibility = 'hidden'
      this.overlay.blur()
      this.highlight.style.visibility = 'hidden'
    } else {
      this.mobileSheet.style.display = 'none'
      this.mobileDoneBento.style.display = 'none'
      this.mobileText.blur()
      this.mobileText.value = ''
    }
    this.mode = 'desktop'
    this.mobileLayout = undefined
    this.pendingCaretPosition = undefined
    // Notify app after overlay state updates
    if (stopCb) stopCb()
  }

  isCurrentlyEditing(): boolean {
    return this.isEditing
  }

  isMobileEditing(): boolean {
    return this.isEditing && this.mode === 'mobile'
  }

  consumePointerStopFlag(): boolean {
    const wasPointer = this.stoppedByPointer
    this.stoppedByPointer = false
    return wasPointer
  }

  // A stop flag belongs to exactly one gesture: the press that blurred the
  // editor. A new gesture starting means the old flag is stale — clear it
  // before this press's own blur (browsers move focus after mousedown
  // handlers run) can set a fresh one.
  clearPointerStopFlag(): void {
    this.stoppedByPointer = false
  }

  getCurrentBlockId(): string | undefined {
    return this.currentBlockId
  }

  setCamera(camera: Camera): void {
    this.camera = camera
  }

  setPaperSpec(spec: PaperSpec): void {
    void spec
  }

  private getSplitForDesktop(): { before: string, after: string } {
    const selection = window.getSelection()
    const text = this.overlay.innerText || ''
    if (!selection || selection.rangeCount === 0) {
      return { before: text, after: '' }
    }
    const range = selection.getRangeAt(0).cloneRange()
    const marker = '\uE000' // private use sentinel unlikely to appear in user text
    const markerNode = document.createTextNode(marker)
    range.insertNode(markerNode)
    const withMarker = this.overlay.innerText || ''
    const idx = withMarker.indexOf(marker)
    markerNode.parentNode?.removeChild(markerNode)
    // Restore original selection
    selection.removeAllRanges()
    selection.addRange(range)
    const caret = idx >= 0 ? idx : text.length
    return {
      before: withMarker.slice(0, caret),
      after: withMarker.slice(caret + 1),
    }
  }

  private insertTextAtCaret(text: string): void {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return
    selection.deleteFromDocument()
    const range = selection.getRangeAt(0)
    const node = document.createTextNode(text)
    range.insertNode(node)
    range.setStartAfter(node)
    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)
    this.syncAfterManualEdit()
  }

  private syncAfterManualEdit(): void {
    if (this.currentBlockId && this.onTextChange) {
      this.onTextChange(this.currentBlockId, this.overlay.innerText)
    }
  }

  private expandSnippetDesktop(boundary: string): boolean {
    if (!this.currentBlockId) return false
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return false
    const range = selection.getRangeAt(0)
    const node = range.startContainer
    if (!(node instanceof Text) || !this.overlay.contains(node)) return false
    const nodeText = node.data
    // Borrow the previous sibling's last character so a trigger at the node
    // start still sees its true left context (BR counts as a newline).
    const prev = node.previousSibling
    const prevChar = prev ? (prev.nodeName === 'BR' ? '\n' : (prev.textContent ?? '').slice(-1)) : ''
    const hit = findSnippetAtCaret(prevChar + nodeText, prevChar.length + range.startOffset, new Date())
    if (!hit) return false
    const startInNode = hit.start - prevChar.length
    if (startInNode < 0) return false
    const inserted = hit.stamp + boundary
    node.data = nodeText.slice(0, startInNode) + inserted + nodeText.slice(range.startOffset)
    this.setDesktopCaret(node, startInNode + inserted.length)
    this.pendingSnippetRevert = { blockId: this.currentBlockId, inserted, original: hit.trigger + boundary }
    this.syncAfterManualEdit()
    return true
  }

  private revertSnippetDesktop(): boolean {
    const pending = this.pendingSnippetRevert
    if (!pending || pending.blockId !== this.currentBlockId) return false
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return false
    const range = selection.getRangeAt(0)
    const node = range.startContainer
    if (!(node instanceof Text) || !this.overlay.contains(node)) return false
    const offset = range.startOffset
    if (!node.data.slice(0, offset).endsWith(pending.inserted)) return false
    const start = offset - pending.inserted.length
    node.data = node.data.slice(0, start) + pending.original + node.data.slice(offset)
    this.setDesktopCaret(node, start + pending.original.length)
    this.pendingSnippetRevert = undefined
    this.syncAfterManualEdit()
    return true
  }

  private setDesktopCaret(node: Text, offset: number): void {
    const selection = window.getSelection()
    if (!selection) return
    const range = document.createRange()
    range.setStart(node, offset)
    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  private applyMobileSnippet(triggerEnd: number, boundary: string, tailStart: number): boolean {
    if (!this.currentBlockId) return false
    const value = this.mobileText.value
    const hit = findSnippetAtCaret(value, triggerEnd, new Date())
    if (!hit) return false
    const inserted = hit.stamp + boundary
    this.mobileText.value = value.slice(0, hit.start) + inserted + value.slice(tailStart)
    const caret = hit.start + inserted.length
    this.mobileText.setSelectionRange(caret, caret)
    this.pendingSnippetRevert = { blockId: this.currentBlockId, inserted, original: hit.trigger + boundary }
    return true
  }

  private expandSnippetMobileAfterBoundary(): void {
    const caret = this.mobileText.selectionStart ?? -1
    if (caret < 1 || caret !== (this.mobileText.selectionEnd ?? -1)) {
      this.pendingSnippetRevert = undefined
      return
    }
    const boundary = this.mobileText.value[caret - 1]
    if (!this.applyMobileSnippet(caret - 1, boundary, caret)) {
      this.pendingSnippetRevert = undefined
    }
  }

  private revertSnippetMobile(): boolean {
    const pending = this.pendingSnippetRevert
    this.pendingSnippetRevert = undefined
    if (!pending || pending.blockId !== this.currentBlockId) return false
    const caret = this.mobileText.selectionStart ?? -1
    if (caret < pending.inserted.length || caret !== (this.mobileText.selectionEnd ?? -1)) return false
    const value = this.mobileText.value
    if (!value.slice(0, caret).endsWith(pending.inserted)) return false
    const start = caret - pending.inserted.length
    this.mobileText.value = value.slice(0, start) + pending.original + value.slice(caret)
    const nextCaret = start + pending.original.length
    this.mobileText.setSelectionRange(nextCaret, nextCaret)
    if (this.currentBlockId && this.onTextChange) {
      this.onTextChange(this.currentBlockId, this.mobileText.value)
    }
    this.syncMobileLayout()
    return true
  }

  private commitSnippetAtEnd(): void {
    if (!this.currentBlockId || !this.onTextChange) return
    const text = this.mode === 'mobile' ? this.mobileText.value : this.overlay.innerText
    const hit = findSnippetAtCaret(text, text.length, new Date())
    if (!hit) return
    this.onTextChange(this.currentBlockId, text.slice(0, hit.start) + hit.stamp)
  }

  private insertTextIntoMobileEditor(text: string): void {
    const start = this.mobileText.selectionStart ?? this.mobileText.value.length
    const end = this.mobileText.selectionEnd ?? start
    const next = this.mobileText.value.slice(0, start) + text + this.mobileText.value.slice(end)
    this.mobileText.value = next
    const caret = start + text.length
    this.mobileText.setSelectionRange(caret, caret)
    if (this.currentBlockId && this.onTextChange) {
      this.onTextChange(this.currentBlockId, this.mobileText.value)
    }
    this.syncMobileLayout()
  }

  updateEditingPosition(block: Block, camera: Camera): void {
    if (!this.isEditing || this.currentBlockId !== block.ided[0]) return
    if (this.mode === 'mobile') return
    this.camera = camera
    const baseX = toPixels(block.xyzw[0])
    const baseY = toPixels(block.xyzw[1])
    const padX = TEXT_PADDING_X_UNITS * GRID_SIZE
    const halfLeading = (LINE_HEIGHT - FONT_SIZE) / 2
    const blockWidth = toPixels(block.xyzw[3])
    const left = baseX * this.camera.scale + this.camera.tx
    const top = (baseY + TEXT_BASELINE_SHIFT_PX) * this.camera.scale + this.camera.ty
    const padTop = TEXT_PADDING_TOP_PX - (halfLeading + CSS_BASELINE_ADJUST_PX)
    this.overlay.style.left = `${left}px`
    this.overlay.style.top = `${top}px`
    this.overlay.style.width = `${blockWidth}px`
    this.overlay.style.paddingLeft = `${padX}px`
    this.overlay.style.paddingRight = `${padX}px`
    this.overlay.style.paddingTop = `${padTop}px`
    this.overlay.style.paddingBottom = `${TEXT_PADDING_BOTTOM_PX - TEXT_BOTTOM_TRIM_PX}px`
    this.overlay.style.transform = `scale(${this.camera.scale})`
    this.updateHighlight(block, this.camera)
  }

  private updateHighlight(block: Block, camera: Camera): void {
    if (!this.isEditing || this.currentBlockId !== block.ided[0]) return
    if (this.mode === 'mobile') return
    const scale = camera.scale || 1
    const baseX = toPixels(block.xyzw[0])
    const baseY = toPixels(block.xyzw[1])
    const blockWidth = toPixels(block.xyzw[3])
    const left = baseX * scale + camera.tx
    const top = baseY * scale + camera.ty
    const rect = this.overlay.getBoundingClientRect()
    const innerHeight = rect.height > 0 ? rect.height / scale : LINE_HEIGHT
    const blockHeight = Math.max(LINE_HEIGHT, innerHeight + TEXT_PADDING_TOP_PX + TEXT_PADDING_BOTTOM_PX - TEXT_BOTTOM_TRIM_PX)
    this.highlight.style.left = `${left}px`
    this.highlight.style.top = `${top}px`
    this.highlight.style.width = `${blockWidth}px`
    this.highlight.style.height = `${blockHeight}px`
    this.highlight.style.transform = `scale(${scale})`
    this.highlight.style.visibility = 'visible'
  }

  private applyPendingCaretPosition(): void {
    if (!this.pendingCaretPosition) return
    if (!this.isEditing) {
      this.pendingCaretPosition = undefined
      return
    }
    const caret = this.pendingCaretPosition
    this.pendingCaretPosition = undefined
    const selection = window.getSelection()
    if (!selection) return
    const range = this.caretRangeFromViewportPoint(caret.clientX, caret.clientY)
    if (range) {
      selection.removeAllRanges()
      selection.addRange(range)
      return
    }
    const fallbackRange = document.createRange()
    fallbackRange.selectNodeContents(this.overlay)
    const overlayRect = this.overlay.getBoundingClientRect()
    const towardEnd = caret.clientY >= overlayRect.top + overlayRect.height / 2
    fallbackRange.collapse(towardEnd)
    selection.removeAllRanges()
    selection.addRange(fallbackRange)
  }

  private caretRangeFromViewportPoint(clientX: number, clientY: number): Range | null {
    const overlayRect = this.overlay.getBoundingClientRect()
    if (overlayRect.width === 0 || overlayRect.height === 0) {
      return null
    }
    const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value))
    const margin = 1
    const clampedX = clamp(clientX, overlayRect.left + margin, overlayRect.right - margin)
    const clampedY = clamp(clientY, overlayRect.top + margin, overlayRect.bottom - margin)
    const doc: any = document
    let range: Range | null = null
    if (typeof doc.caretRangeFromPoint === 'function') {
      range = doc.caretRangeFromPoint(clampedX, clampedY)
      if (range && this.overlay.contains(range.startContainer)) {
        return range
      }
    }
    if (typeof doc.caretPositionFromPoint === 'function') {
      const position = doc.caretPositionFromPoint(clampedX, clampedY)
      if (position && this.overlay.contains(position.offsetNode)) {
        const r = document.createRange()
        r.setStart(position.offsetNode, position.offset)
        r.collapse(true)
        return r
      }
    }
    return null
  }

  private startDesktopEditing(block: Block): void {
    // Position overlay over block (world -> screen)
    const baseX = toPixels(block.xyzw[0])
    const baseY = toPixels(block.xyzw[1])
    const padX = TEXT_PADDING_X_UNITS * GRID_SIZE
    const halfLeading = (LINE_HEIGHT - FONT_SIZE) / 2 // CSS half-leading compensation
    const innerLeft = (baseX + padX) * this.camera.scale + this.camera.tx
    // Push CSS baseline up: subtract half-leading and a small calibration to match canvas
    const innerTop = (baseY + TEXT_PADDING_TOP_PX + TEXT_BASELINE_SHIFT_PX - (halfLeading + CSS_BASELINE_ADJUST_PX)) * this.camera.scale + this.camera.ty
    // Width in world pixels; CSS transform will scale it to screen width
    const innerW = Math.max(0, toPixels(block.xyzw[3]) - 2 * padX)
    this.overlay.style.left = `${innerLeft}px`
    this.overlay.style.top = `${innerTop}px`
    this.overlay.style.width = `${innerW}px`
    // Apply camera scale via transform to match canvas rendering exactly
    this.overlay.style.transform = `scale(${this.camera.scale})`
    this.overlay.style.visibility = 'visible'
    
    // Set content and focus (let browser place caret; no force-select-all)
    this.overlay.innerText = block.text
    this.overlay.focus()
    this.updateHighlight(block, this.camera)
    requestAnimationFrame(() => {
      if (!this.isEditing || this.currentBlockId !== block.ided[0]) return
      this.updateHighlight(block, this.camera)
      this.applyPendingCaretPosition()
    })
  }

  private startMobileEditing(block: Block): void {
    this.highlight.style.visibility = 'hidden'
    this.overlay.style.visibility = 'hidden'
    this.mobileText.value = block.text
    this.mobileSheet.style.display = 'flex'
    this.mobileDoneBento.style.display = 'flex'
    this.applyMobileLayout()
    this.mobileText.focus({ preventScroll: true } as any)
    const caret = this.mobileCaretAtStart ? 0 : this.mobileText.value.length
    try {
      this.mobileText.setSelectionRange(caret, caret)
    } catch {
      // Ignore selection errors on some platforms
    }
    this.mobileCaretAtStart = false
    this.updateMobileTextHeight()
  }

  private applyMobileLayout(): void {
    const layout = this.computeMobileLayout()
    this.mobileLayout = layout
    const marginPx = GRID_SIZE * 2
    this.mobileSheet.style.top = `calc(${marginPx}px + env(safe-area-inset-top, 0px))`
    this.mobileSheet.style.bottom = 'auto'
    this.mobileSheet.style.left = '50%'
    this.mobileSheet.style.transform = 'translateX(-50%)'
    this.mobileSheet.style.maxHeight = `${layout.maxHeight}px`
    this.updateMobileTextHeight()
  }

  private computeMobileLayout(): { initialHeight: number, maxHeight: number } {
    const vv: any = (window as any).visualViewport
    const viewportHeight = vv?.height ?? window.innerHeight
    const marginPx = GRID_SIZE * 2
    const safeBottom = 0 // env() handled in CSS; JS uses visualViewport height
    const doneReserve = MOBILE_EDITOR_DONE_BENTO_HEIGHT_PX + MOBILE_EDITOR_DONE_GAP_PX
    const availableHeight = Math.max(0, viewportHeight - marginPx * 2 - safeBottom - doneReserve)
    const initialHeight = Math.min(window.innerHeight * 0.6, availableHeight)
    const maxHeight = availableHeight
    return { initialHeight, maxHeight }
  }

  private syncMobileLayout(): void {
    if (!this.isEditing || this.mode !== 'mobile') return
    this.applyMobileLayout()
  }

  private updateMobileTextHeight(): void {
    if (!this.mobileLayout) return
    const sheetPaddingY = 32 // 16px top + 16px bottom
    const maxTextHeight = Math.max(64, this.mobileLayout.maxHeight - sheetPaddingY)
    this.mobileText.style.height = 'auto'
    const desired = Math.min(maxTextHeight, Math.max(LINE_HEIGHT * 2, this.mobileText.scrollHeight))
    this.mobileText.style.height = `${desired}px`
    this.mobileText.style.maxHeight = `${maxTextHeight}px`
    this.updateMobileDoneBentoPosition()
  }

  private updateMobileDoneBentoPosition(): void {
    if (this.mobileDoneBento.style.display === 'none') return
    const sheetRect = this.mobileSheet.getBoundingClientRect()
    if (sheetRect.width <= 0 || sheetRect.height <= 0) return
    this.mobileDoneBento.style.top = `${sheetRect.bottom + MOBILE_EDITOR_DONE_GAP_PX}px`
  }
}
